<template>
  <v-dialog
    v-model="openCloseDialog"
    persistent
    :max-width="dialogMaxWidth"
  >
    <v-card>
      <v-card-title>
        <span class="text-h5">{{ dialogTitle }}</span>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-form ref="importForm">
          <div v-if="isErrorWithHeader">
            <div v-if="hasAnyErrors && !hasPreview">
              <h3 class="mb-2">Errors</h3>
              <v-data-table
                v-if="generalErrors.length > 0"
                :headers="headers"
                :items="generalErrors"
                class="elevation-1"
                :hide-default-footer="true"
              ></v-data-table>
              <div v-if="isVoucherSpecificError">
                <HandleVoucherSequenceError
                  :voucherSequenceErrors="additionalErrors" />
              </div>
              <div v-if="isWarrantSpecificError">
                <HandleWarSequenceError
                  :warErrors="additionalErrors" />
              </div>
              <div v-if="isTaxLevySpecificError">
                <HandleTaxLevyError
                  :taxLevyErrors="additionalErrors"
                  @updatedImportTaxLevy="fetchAndEmitImportedTaxLevy"
                  />
              </div>
              <div v-if="isGlSpecificError">
                <HandleGlError
                  :glErrors="additionalErrors" />
              </div>
            </div>
            <div v-if="hasPreview">
              <WarrantImportPreview
                :preview="preview"
                :allowSeqErrorImport="!!allowImpWithErrInWarNoSeq"
                :hasBadDates="hasBadDates"
              />
            </div>
          </div>
          <div v-else class="blue lighten-5 rounded-lg pa-6">
            {{ errors }}
          </div>
        </v-form>
        </v-container>
      </v-card-text>
      <v-card-actions>
         <v-btn
          text color="blue darken-1"
          :disabled="!showProceedBtn"
          @click="createDependency"
          >
            {{ proceedBtnLabel }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="blue darken-1"
          text
          @click="openCloseDialog = false"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex';
import HandleWarSequenceError from './HandleWarSequenceError.vue';
import HandleVoucherSequenceError from './HandleVoucherSequenceError.vue';
import HandleTaxLevyError from './HandleTaxLevyError.vue';
import HandleGlError from './HandleGlError.vue';
import WarrantImportPreview from './WarrantImportPreview.vue';
import { willCreatePreviewRows } from './warrantPreview.utils';

const initdata = () => ({
  headers: [
    { text: 'Entity', value: 'errorKey' },
    { text: 'Error Data', value: 'data' },
    { text: 'Error message', value: 'message' },
  ],
});

export default {
  name: 'HandleImportError',
  components: {
    HandleVoucherSequenceError,
    HandleWarSequenceError,
    HandleTaxLevyError,
    HandleGlError,
    WarrantImportPreview,
  },
  props: {
    dialog: {
      type: Boolean,
      default: () => false,
    },
    errorMode: {
      type: String,
      default: () => '',
    },
    errors: {
      type: [Array, Object],
      default: () => [],
    },
    createDependencies: {
      type: Boolean,
      default: () => false,
    },
    showDependencyBtn: {
      type: Boolean,
      default: () => true,
    },
  },
  data: () => initdata(),
  computed: {
    ...mapState({
      warSettingItem: (state) => state.SystemConfig.warSettingItem,
      odSettingItem: (state) => state.SystemConfig.odSettingItem,
    }),
    openCloseDialog: {
      get() {
        return this.dialog;
      },
      set(value) {
        this.$emit('openCloseDialog', value);
      },
    },
    generalErrors() {
      return this.errors.generalErrors || [];
    },
    isVoucherSpecificError() {
      return this.errors.additionalErrors && this.errors.additionalErrors.key === 'voucher';
    },
    isWarrantSpecificError() {
      return this.errors.additionalErrors && this.errors.additionalErrors.key === 'warrants';
    },
    isTaxLevySpecificError() {
      return this.errors.additionalErrors && this.errors.additionalErrors.key === 'taxLevy';
    },
    isGlSpecificError() {
      return this.errors.additionalErrors && this.errors.additionalErrors.key === 'gl';
    },
    additionalErrors() {
      return (this.errors.additionalErrors || {}).errors || [];
    },
    isErrorWithHeader() {
      return this.errorMode === 'errorWithHeader';
    },
    allowImpWithErrInWarNoSeq() {
      return this.warSettingItem && this.warSettingItem.allowImpWithErrInWarNoSeq;
    },
    allowImpWithSeqErrInOD() {
      return this.odSettingItem && this.odSettingItem.allowImpWithSeqErrInOD;
    },
    preview() {
      return Array.isArray(this.errors.preview) ? this.errors.preview : [];
    },
    hasPreview() {
      return this.preview.length > 0;
    },
    willCreateCount() {
      return willCreatePreviewRows(this.preview, {
        allowSeqErrorImport: !!this.allowImpWithErrInWarNoSeq,
        hasBadDates: this.hasBadDates,
      }).length;
    },
    hasBadDates() {
      return !!this.errors.hasBadDates;
    },
    hasAnyErrors() {
      if (this.generalErrors.length > 0) return true;
      if (this.additionalErrors && this.additionalErrors.length > 0) return true;
      return !!this.errors.hasErrors;
    },
    dialogMaxWidth() {
      // The grouped warrant preview carries 9 columns; give it more room.
      return this.hasPreview ? '80vw' : '60vw';
    },
    dialogTitle() {
      if (this.hasPreview) return 'Import Preview';
      return 'Errors while importing data';
    },
    proceedBtnLabel() {
      if (this.hasPreview) {
        const n = this.willCreateCount;
        return `Create ${n} warrant${n === 1 ? '' : 's'}`;
      }
      return 'Proceed by creating';
    },
    hasSeqError() {
      const k = (this.errors.additionalErrors || {}).key;
      return k === 'warrants' && (this.additionalErrors || []).length > 0;
    },
    hasVoucherSeqError() {
      const k = (this.errors.additionalErrors || {}).key;
      return k === 'voucher' && (this.additionalErrors || []).length > 0;
    },
    showProceedBtn() {
      if (!this.isErrorWithHeader) return false;
      if (this.hasBadDates) return false;
      if (this.hasSeqError && !this.allowImpWithErrInWarNoSeq) return false;
      if (this.allowImpWithErrInWarNoSeq && this.hasSeqError && this.generalErrors.length === 0) return true;
      if (this.hasVoucherSeqError && !this.allowImpWithSeqErrInOD) return false;
      if (this.allowImpWithSeqErrInOD && this.hasVoucherSeqError && this.generalErrors.length === 0) return true;
      if (!this.hasAnyErrors) return true;
      return !!this.showDependencyBtn;
    },
  },
  methods: {
    createDependency() {
      if (this.$refs.importForm.validate()) {
        this.$emit('createDependencies', true);
        this.$emit('openCloseDialog', false);
      }
    },
    fetchAndEmitImportedTaxLevy(value) {
      let additionalImportedData = {};
      if (Array.isArray(value) && value.length > 0) {
        additionalImportedData = {
          taxLevyData: value,
        };
        this.$emit('getAdditionalImportedData', additionalImportedData);
      }
    },
  },
};
</script>
